// Plain-language summaries. The underlying research records are unchanged.

export type Study = {
  slug: string; number: string; name: string; title: string;
  question: string; method: string; finding: string; limitation: string;
  status: string; cutoff: string; cutoffLabel: string;
  resources: { label: string; href: string }[];
};
export const studies: Study[] = [
  {
    slug: "part-i", number: "I", name: "Testing AI",
    title: "What can AI help someone do?",
    question: "We tested whether AI could help plan manipulation. We did not run a campaign or contact voters.",
    method: "We asked AI systems to help with planning and sample content in a closed test setting. We examined some records more closely and separately tried changing a model. These were different tests, not one experiment with a shared success rate.",
    finding: "The early records describe help with planning and sample content. Some closer checks found planning output even when other requests were refused.",
    limitation: "This does not show that a campaign was carried out, that the system could reliably act on its own, or that anyone was persuaded.",
    status: "Early tests", cutoff: "2026-08-28", cutoffLabel: "28 August 2026",
    resources: [
      { label: "Cost summary", href: "research/part-i/#costs" },
      { label: "Publication policy", href: "about/#publication-policy" },
    ],
  },
  {
    slug: "part-ii", number: "II", name: "Measuring manipulation",
    title: "Can we measure harmful manipulation?",
    question: "There are useful AI tests. The harder question is whether their results can be compared, and what they tell us about a system used by people.",
    method: "We reviewed existing tests and their published results. We kept track of which model was tested, under what conditions, and what the test actually measured. The Registry holds those records separately from this website's summaries.",
    finding: "The review did not identify a widely accepted and independently checked way to measure harmful manipulation across deployed AI systems.",
    limitation: "That does not mean relevant tests do not exist. It means the records we reviewed do not justify combining their results into a single manipulation or safety score.",
    status: "Review in progress", cutoff: "2026-08-28", cutoffLabel: "28 August 2026",
    resources: [
      { label: "How to read the chart", href: "registry/#chart-method" },
      { label: "Measurement notes", href: "registry/evidence/#measurement-gap" },
    ],
  },
  {
    slug: "part-iii", number: "III", name: "Election cases",
    title: "What has happened in elections?",
    question: "The reviewed sources document attempts to deceive and impersonate people. They tell us much less about whether those attempts changed votes.",
    method: "We brought together selected election cases and linked each claim to its source. We distinguished evidence of an operation from evidence that people saw it, responded to it, or changed a decision.",
    finding: "The records describe influence attempts and harms, including impersonation, harassment and confusion. These matter even when their effect on an election result is unknown.",
    limitation: "This collection is not a count of all incidents. It cannot establish how common they are, how fast they are growing, or whether AI changed the outcome of a national election.",
    status: "Case review in progress", cutoff: "2026-08-28", cutoffLabel: "28 August 2026",
    resources: [
      { label: "Election case records", href: "https://huggingface.co/datasets/apol/ai-election-manipulation-cases" },
      { label: "Sources", href: "references/" },
    ],
  },
  {
    slug: "part-iv", number: "IV", name: "Reducing harm",
    title: "What could reduce the harm?",
    question: "A safeguard should be judged by the harm it reduces, not just by whether someone has adopted it.",
    method: "We reviewed the evidence linked to 118 recorded ways of putting safeguards into practice. The sources include studies with people, technical tests and institutional documents. Some were checked more closely than others.",
    finding: "There is support for particular measures in particular settings. What works depends on what is being changed, when help arrives, and who it protects.",
    limitation: "The 118 records are not 118 proven policies. The review does not show that any package of measures protects an election as a whole.",
    status: "Policy review in progress", cutoff: "2026-09-01", cutoffLabel: "1 September 2026",
    resources: [
      { label: "Reviewed sources", href: "references/?collection=p4-review" },
      { label: "Review spreadsheet", href: "research/p4-source-linked-review-20260901.xlsx" },
    ],
  },
];
export const studyForPath = (path: string) => studies.find(study => path === `research/${study.slug}`);
